import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download, Share2, ArrowRight, Home } from 'lucide-react';
import { api, BACKEND_URL } from '@/lib/api';
import Logo from '@/components/Logo';
import Footer from '@/components/Footer';
import ShareCertModal from '@/components/ShareCertModal';
import { formatMoney } from '@/lib/format';

/**
 * Public certificate view — no auth required. Designed for social share preview + web viewers.
 */
export default function PublicCertificate() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState('');
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    api.get(`/public/certificates/${id}`)
      .then(r => setCert(r.data))
      .catch(() => setErr('Certificate not found'));
  }, [id]);

  const imageUrl = `${BACKEND_URL}/api/public/certificates/${id}/image`;
  const pdfUrl = `${BACKEND_URL}/api/public/certificates/${id}/download`;

  if (err) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="label-sm mb-3">Certificate</div>
          <h1 className="font-display text-3xl">{err}</h1>
          <Link to="/" className="btn-ghost mt-6 inline-flex"><Home size={14} className="inline mr-1" /> Back home</Link>
        </div>
      </div>
    );
  }
  if (!cert) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="flex items-center gap-3 text-zinc-500 font-mono text-sm"><span className="pulse-dot" /> LOADING CERTIFICATE</div>
    </div>;
  }

  const titleByType = {
    payout: 'Performance Reward',
    phase_1_passed: 'Phase 1 Passed',
    phase_2_passed: 'Phase 2 Passed',
    funded: 'Performance Trader',
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5 bg-black/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo size={24} /></Link>
          <div className="flex items-center gap-2">
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-ghost btn-sm" data-testid="public-download">
              <Download size={12} className="inline mr-1" /> PDF
            </a>
            <button className="btn-primary btn-sm" onClick={() => setShowShare(true)} data-testid="public-share">
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <div className="label-sm mb-2">Official Traders Spring Certificate</div>
        <h1 className="font-display text-4xl sm:text-5xl">{titleByType[cert.cert_type] || 'Traders Spring Certificate'}</h1>
        <p className="text-zinc-400 mt-3">
          This certificate was issued to <span className="text-white font-medium">{cert.trader_name}</span>{" "}
          on <span className="font-mono">{new Date(cert.issued_at).toLocaleDateString()}</span>.
        </p>

        <div className="card-tech rounded-xl p-4 mt-8 overflow-hidden">
          <img src={imageUrl} alt="Traders Spring Certificate" className="w-full rounded-lg block" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <Info k="Certificate ID" v={cert.certificate_id.toUpperCase()} mono />
          <Info k="Issued" v={new Date(cert.issued_at).toLocaleString()} />
          <Info k="Type" v={(cert.cert_type || 'payout').replace(/_/g, ' ').toUpperCase()} />
          {cert.cert_type === 'payout' && cert.amount != null && (
            <Info k="Reward amount" v={`$${formatMoney(cert.amount)}`} highlight />
          )}
          {cert.plan_name && <Info k="Program" v={cert.plan_name} />}
          {cert.account_size && <Info k="Simulated account size" v={`$${Number(cert.account_size).toLocaleString()}`} />}
        </div>

        <div className="mt-12 card-tech rounded-lg p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-xl">Want your own certificate?</h3>
            <p className="text-sm text-zinc-500 mt-1">Start your Traders Spring evaluation from $20 with the Split Fee Program.</p>
          </div>
          <Link to="/plans" className="btn-primary" data-testid="public-cta">
            Start Evaluation <ArrowRight size={16} />
          </Link>
        </div>
      </main>

      <Footer />
      {showShare && <ShareCertModal cert={cert} onClose={() => setShowShare(false)} />}
    </div>
  );
}

function Info({ k, v, mono, highlight }) {
  return (
    <div className="card-tech rounded-lg p-4">
      <div className="label-xs">{k}</div>
      <div className={`mt-1 ${mono ? 'font-mono' : ''} ${highlight ? 'text-[#00ff66] text-2xl font-mono' : ''}`}>{v}</div>
    </div>
  );
}
